import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import JobCard from "./job-card";
import EditJobModal from "./edit-job-modal";
import { Typography } from "@material-ui/core";
import { safetyWiseApi } from "utils/safetyWiseApi";
// import Skeleton from '@material-ui/lab/Skeleton'
// import { Page, PageContent } from "frame/components";
import {
	Page,
	PageContent,
	Pagination,
	PaginationHeader,
} from "frame/components";
import { getPageResults, canPinJobs } from "utils";
import { useRouter, isNotNullOrEmpty, getCurrentTimestamp } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.white,
	},
	heading: {
		marginTop: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	body: {
		margin: theme.spacing(3, 0),
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
	embeddedContent: {
		paddingTop: theme.spacing(1),
	},
	filter: { display: "flex" },
}));

// used in site summary
// const JobSiteList = ({ onEdit, onDelete, currentUserId, role, hasNetwork }) => {
const JobSiteList = ({
	onEdit,
	onDelete,
	currentUserId,
	role,
	hasNetwork,
	pagination,
	onGetJobList,
	flags,
	currentOrganisationGUID,
	variant = "page",
	/** When embedded under site summary, the matched route can be `/sites` (no :siteId param); pass explicit id. */
	siteId: siteIdProp,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const siteId = siteIdProp || match?.params?.siteId;
	const isEmbedded = variant === "embedded";

	const jobList = getPageResults(pagination);

	const refreshJobList = useCallback(() => {
		const page = pagination?.pageNumber || pagination?.page || 1;
		const pageSize = pagination?.queryPageSize || pagination?.pageSize || 5;
		onGetJobList({
			page,
			pageSize,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "siteId",
			columnValue: siteId,
		});
	}, [onGetJobList, pagination, siteId]);

	/* Pagination's on-load effect does not depend on siteId, so shared jobs state can
	   show another site's page until an explicit refetch. */
	useEffect(() => {
		if (!siteId || !currentOrganisationGUID) {
			return;
		}
		onGetJobList({
			page: 1,
			pageSize: 5,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "siteId",
			columnValue: siteId,
		});
	}, [siteId, currentOrganisationGUID, onGetJobList]);

	const handleToggleSitePin = useCallback(
		(job) => {
			if (!currentOrganisationGUID || !job) {
				return;
			}
			const nextPinned = !job.pinnedToSite;
			const body = {
				id: job.id,
				title: job.title || "",
				createdAt: job.createdAt,
				type: job.type || "",
				customerId: job.customerId || "",
				siteId: job.siteId || siteId || "",
				staffId: job.staffId || "",
				notes: job.notes || "",
				attachments: job.attachments || [],
				organisationId: job.organisationId || currentOrganisationGUID,
				latitude: job.latitude ?? "",
				longitude: job.longitude ?? "",
				timestamp: job.timestamp || getCurrentTimestamp(),
				creatorId: job.creatorId || "",
				pinnedToSite: nextPinned,
				pinnedToCustomer: !!job.pinnedToCustomer,
				pinnedToStaff: !!job.pinnedToStaff,
			};
			safetyWiseApi
				.post(`${currentOrganisationGUID}/job/createorupdate`, body)
				.then(() => refreshJobList())
				.catch(() => {
					// eslint-disable-next-line no-alert
					window.alert("Could not update pin. Check your connection and try again.");
				});
		},
		[currentOrganisationGUID, siteId, refreshJobList]
	);
	// const jobList = useSelector(({ jobs }) =>
	// 	jobs.pagination?.results.filter((j) => j.siteId === siteId)
	// );

	const empty = jobList && jobList.length <= 0;
	const allowPin = canPinJobs(role);

	const listContent = (
		<PageContent pad className={isEmbedded ? classes.embeddedContent : undefined}>
			{!isEmbedded && (
				<Typography variant="h2" className={classes.heading}>
					Photos / Notes
				</Typography>
			)}

			<div className={classes.body}>
				<PaginationHeader pagination={pagination} />

				{isNotNullOrEmpty(jobList) &&
					jobList.map((job, index) => (
						<JobCard
							key={job.id || index}
							job={job}
							role={role}
							currentUserId={currentUserId}
							hasNetwork={hasNetwork}
							onEdit={onEdit}
							onDelete={onDelete}
							enableSitePin={allowPin}
							onToggleSitePin={allowPin ? handleToggleSitePin : undefined}
						/>
					))}
			</div>
			{empty && (
				<Typography variant="body1">
					There are currently no photos or notes.
				</Typography>
			)}

			<Pagination
				disabledOnLoadFetch={flags.create === "processing"}
				onGetList={(paginationParams) =>
					onGetJobList({
						...paginationParams,
						columnName: "siteId",
						columnValue: siteId,
					})
				}
				pagination={pagination}
				orderBy="createdAt"
				ascending={false}
			/>
		</PageContent>
	);

	if (isEmbedded) {
		return (
			<div className={classes.embeddedRoot}>
				{listContent}
				<EditJobModal />
			</div>
		);
	}

	return (
		<Page className={classes.root} title="Photos / Notes">
			{listContent}

			<EditJobModal />
		</Page>
	);
};

JobSiteList.propTypes = {
	currentUserId: PropTypes.string.isRequired,
	role: PropTypes.string.isRequired,
	onAdd: PropTypes.func,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSearchChanged: PropTypes.func.isRequired,
	hasNetwork: PropTypes.bool.isRequired,
	pagination: PropTypes.object.isRequired,
	flags: PropTypes.object.isRequired,
	searchFilter: PropTypes.string,
	onGetJobList: PropTypes.func.isRequired,
	currentOrganisationGUID: PropTypes.string,
	variant: PropTypes.oneOf(["page", "embedded"]),
	siteId: PropTypes.string,
};

export default JobSiteList;
